import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";

import swal from "sweetalert2";
import { InventarioIAService } from "../inventario-ia.service";
import {
  CatCicloVida,
  InventarioIA,
} from "src/app/shared/model/iso42001/inventario-ia.model";

@Component({
  selector: "app-gestion-inventario-ia",
  standalone: true,
  templateUrl: "./gestion-inventario-ia.component.html",
  styleUrls: ["./gestion-inventario-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class GestionInventarioIAComponent implements OnInit {
  inventarios: InventarioIA[] = [];
  ciclosVida: CatCicloVida[] = [];
  loading = false;

  filtro: InventarioIA = { nombreIa: "", estatus: "", idCicloVida: null };

  // Paginación client-side (10 por página). A diferencia de la pantalla de
  // consulta, aquí SÍ se listan los inactivos: hay que poder reactivarlos.
  tamPagina = 10;
  paginaActual = 1;

  constructor(
    private service: InventarioIAService,
    private router: Router,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargarCatalogos();
    this.consultar();
  }

  cargarCatalogos(): void {
    this.service.ciclosVida().subscribe({
      next: (res) => {
        this.ciclosVida = res.ciclosVida || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.ciclosVida = [];
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * @param reiniciarPagina false al recargar tras eliminar o cambiar estatus,
   * para no devolver al usuario a la página 1 desde donde estaba trabajando.
   */
  consultar(reiniciarPagina = true): void {
    this.loading = true;
    this.service.consultar(this.filtro).subscribe({
      next: (res) => {
        this.inventarios = res.inventarios || [];
        if (reiniciarPagina) {
          this.paginaActual = 1;
        }
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  // ── Paginación ──────────────────────────────────────────────────────────────
  inventariosPagina(): InventarioIA[] {
    const inicio = (this.paginaNorm() - 1) * this.tamPagina;
    return this.inventarios.slice(inicio, inicio + this.tamPagina);
  }

  totalPaginas(): number {
    const n = this.inventarios.length;
    return n > 0 ? Math.ceil(n / this.tamPagina) : 1;
  }

  /** Acota la página al rango válido: al borrar el último de la página, se recorre sola. */
  paginaNorm(): number {
    return Math.min(Math.max(this.paginaActual, 1), this.totalPaginas());
  }

  rangoInicio(): number {
    return this.inventarios.length === 0 ? 0 : (this.paginaNorm() - 1) * this.tamPagina + 1;
  }

  rangoFin(): number {
    return Math.min(this.paginaNorm() * this.tamPagina, this.inventarios.length);
  }

  irPagina(p: number): void {
    const total = this.totalPaginas();
    if (p < 1 || p > total || p === this.paginaNorm()) {
      return;
    }
    this.paginaActual = p;
    this.cdr.detectChanges();
  }

  primeraPagina(): void { this.irPagina(1); }
  paginaAnterior(): void { this.irPagina(this.paginaNorm() - 1); }
  paginaSiguiente(): void { this.irPagina(this.paginaNorm() + 1); }
  ultimaPagina(): void { this.irPagina(this.totalPaginas()); }

  getPaginationItems(): Array<number | string> {
    const total = this.totalPaginas();
    if (total <= 1) {
      return [1];
    }
    const a = this.paginaNorm();
    const set = new Set<number>([1, total, a - 1, a, a + 1]);
    const ord = Array.from(set).filter((p) => p >= 1 && p <= total).sort((x, y) => x - y);
    const items: Array<number | string> = [];
    for (let k = 0; k < ord.length; k++) {
      const p = ord[k];
      const prev = ord[k - 1];
      if (prev && p - prev > 1) {
        items.push("e" + prev);
      }
      items.push(p);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === "number";
  }

  limpiar(): void {
    this.filtro = { nombreIa: "", estatus: "", idCicloVida: null };
    this.consultar();
  }

  nuevo(): void {
    this.router.navigate(["/admin/inventario-ia/alta"]);
  }

  editar(item: InventarioIA): void {
    this.router.navigate(["/admin/inventario-ia/edicion", item.idInventarioIa]);
  }

  verHistorial(item: InventarioIA): void {
    this.router.navigate(["/admin/inventario-ia/historial", item.idInventarioIa]);
  }

  /** Evaluaciones de monitoreo continuo del activo (ISO/IEC 42001). */
  verMonitoreo(item: InventarioIA): void {
    this.router.navigate(["/admin/inventario-ia/monitoreo", item.idInventarioIa]);
  }

  cambiarEstatus(item: InventarioIA): void {
    const activo = item.estatus === "ACTIVO";
    const nuevo = activo ? "INACTIVO" : "ACTIVO";
    const nombre = this.escapar(item.nombreIa);
    void swal
      .fire({
        icon: "question",
        title: activo ? "Dar de baja el activo" : "Reactivar el activo",
        html: activo
          ? `<strong>${nombre}</strong> pasará a <strong>INACTIVO</strong> y dejará de considerarse en uso. Su historial se conserva.`
          : `<strong>${nombre}</strong> volverá a <strong>ACTIVO</strong>.`,
        showCancelButton: true,
        confirmButtonText: activo ? "Dar de baja" : "Reactivar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#c8102e",
      })
      .then((r) => {
        if (r.isConfirmed) {
          this.service
            .cambiarEstatus(item.idInventarioIa!, nuevo)
            .subscribe(() => this.consultar(false));
        }
      });
  }

  /**
   * Borrado físico. A diferencia de "Dar de baja", el registro desaparece de la
   * base de datos junto con sus impactos y su bitácora, y no hay forma de
   * recuperarlo: por eso el modal arranca con el foco en "Cancelar".
   */
  eliminar(item: InventarioIA): void {
    const folio = item.consecutivo || "IA-" + item.idInventarioIa;
    void swal
      .fire({
        icon: "warning",
        title: "Eliminar activo IA",
        html:
          `Se eliminará <strong>${this.escapar(folio)} · ${this.escapar(item.nombreIa)}</strong> ` +
          `junto con sus impactos y todo su historial de cambios.` +
          `<br><br><strong>Esta acción no se puede deshacer.</strong>`,
        showCancelButton: true,
        focusCancel: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#c8102e",
      })
      .then((r) => {
        if (!r.isConfirmed) {
          return;
        }
        this.service.eliminar(item.idInventarioIa!).subscribe({
          next: (res) => {
            if (res.codigoError) {
              this.errorEliminar(res.mensajeError);
              return;
            }
            void swal.fire({
              icon: "success",
              title: "Activo eliminado",
              text: `${folio} se eliminó correctamente.`,
              timer: 2200,
              showConfirmButton: false,
            });
            this.consultar(false);
          },
          error: () => this.errorEliminar(null),
        });
      });
  }

  private errorEliminar(mensaje: string | null | undefined): void {
    void swal.fire({
      icon: "error",
      title: "No se pudo eliminar",
      text: mensaje || "Ocurrió un error al eliminar el activo. Intenta de nuevo.",
      confirmButtonColor: "#c8102e",
    });
  }

  /** El nombre lo captura el usuario y el modal se pinta como HTML. */
  private escapar(valor: string): string {
    const div = document.createElement("div");
    div.textContent = valor || "";
    return div.innerHTML;
  }

  obtenerPropietario(item: InventarioIA): string {
    return item.areaNombre || item.propietarioExterno || "—";
  }
}
